import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle2, Zap, RefreshCw, ShieldAlert, Bug } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import {
  useAnomalies, useAnomaly, useAnomalyStats, useUpdateAnomaly, useTriggerRules,
  type Anomaly,
} from '@/hooks/useAnomalies';

export default function AnomaliesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<'open' | 'all' | 'resolved'>('open');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const filters = useMemo(() => {
    const f: Record<string, any> = { status: statusFilter, pageSize: 100 };
    if (severityFilter) f.severity = severityFilter;
    return f;
  }, [statusFilter, severityFilter]);

  const { data: list, isLoading } = useAnomalies(filters);
  const { data: stats } = useAnomalyStats();
  const triggerMut = useTriggerRules();

  const fmtSar = (n: number | null) => n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' SAR';

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/fuel')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Button>
          <ShieldAlert className="h-5 w-5 text-red-500" />
          <h1 className="text-lg font-semibold">{t('anomalies.title')}</h1>
          <div className="ml-auto">
            <Button
              variant="outline" size="sm"
              onClick={() => triggerMut.mutate()}
              disabled={triggerMut.isPending}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${triggerMut.isPending ? 'animate-spin' : ''}`} />
              {triggerMut.isPending ? t('anomalies.triggering') : t('anomalies.runRules')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Stats strip */}
        {stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={t('anomalies.stats.open')} value={String(stats.open_count)}
              warn={stats.open_count > 0} icon={<AlertTriangle className="h-4 w-4" />} />
            <StatCard label={t('anomalies.stats.openCritical')} value={String(stats.open_critical)}
              danger={stats.open_critical > 0} icon={<Zap className="h-4 w-4" />} />
            <StatCard label={t('anomalies.stats.atRisk')} value={fmtSar(stats.open_impact_sar)}
              warn={Number(stats.open_impact_sar) > 0} />
            <StatCard label={t('anomalies.stats.weekResolved')}
              value={`${stats.week_resolved} / ${stats.week_total}`}
              icon={<CheckCircle2 className="h-4 w-4" />} />
          </div>
        ) : null}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {(['open', 'resolved', 'all'] as const).map((s) => (
            <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm"
              onClick={() => setStatusFilter(s)}>
              {t(`anomalies.status.${s}`)}
            </Button>
          ))}
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">{t('anomalies.allSeverities')}</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="text-center text-muted-foreground py-12">{t('common.loading')}</div>
        ) : !list?.items.length ? (
          <div className="text-center py-16 border border-dashed border-border rounded-md">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
            <p className="text-muted-foreground">{t('anomalies.empty')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('anomalies.emptyHint')}</p>
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t('anomalies.table.detected')}</Th>
                    <Th>{t('anomalies.table.severity')}</Th>
                    <Th>{t('anomalies.table.title')}</Th>
                    <Th>{t('anomalies.table.vehicle')}</Th>
                    <Th>{t('anomalies.table.driver')}</Th>
                    <Th className="text-right">{t('anomalies.table.impact')}</Th>
                    <Th>{t('anomalies.table.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((a) => (
                    <tr key={a.id} className="border-t border-border hover:bg-accent/50 cursor-pointer"
                      onClick={() => setOpenId(a.id)}>
                      <Td className="text-xs whitespace-nowrap">{new Date(a.detected_at).toLocaleString()}</Td>
                      <Td><SeverityBadge severity={a.severity} /></Td>
                      <Td>
                        <div className="font-medium">{a.title}</div>
                        <div className="text-xs text-muted-foreground">{ruleCodeFromType(a.anomaly_type)}</div>
                      </Td>
                      <Td>{a.vehicle_plate ?? '—'}</Td>
                      <Td>{a.driver_name ?? '—'}</Td>
                      <Td className="text-right">{fmtSar(a.impact_sar)}</Td>
                      <Td><StatusBadge status={a.status} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/30 border-t border-border">
              {t('anomalies.table.summary', { count: list.items.length, total: list.total })}
            </div>
          </div>
        )}
      </main>

      {openId ? <AnomalyDetailSheet id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

function ruleCodeFromType(type: string): string {
  // 'fuel:overfill' -> 'OVERFILL'
  if (type.startsWith('fuel:')) return type.slice(5).toUpperCase();
  return type;
}

function StatCard({ label, value, warn, danger, icon }: {
  label: string; value: string; warn?: boolean; danger?: boolean; icon?: any;
}) {
  const cls = danger ? 'border-red-400 bg-red-50 dark:bg-red-500/10' :
              warn   ? 'border-amber-400 bg-amber-50 dark:bg-amber-500/10' :
                       'border-border bg-card';
  const txt = danger ? 'text-red-700' :
              warn   ? 'text-amber-700' :
                       '';
  return (
    <div className={`rounded-md border p-3 ${cls}`}>
      <div className="text-xs text-muted-foreground flex items-center gap-1">{icon}{label}</div>
      <div className={`text-lg font-semibold mt-1 ${txt}`}>{value}</div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const cls =
    severity === 'critical' ? 'bg-red-500/15 text-red-700 border-red-500/30' :
    severity === 'high'     ? 'bg-orange-500/15 text-orange-700 border-orange-500/30' :
    severity === 'medium'   ? 'bg-amber-500/15 text-amber-700 border-amber-500/30' :
                              'bg-gray-500/15 text-gray-700 border-gray-500/30';
  return <Badge className={cls}>{severity}</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'open') return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">Open</Badge>;
  if (status === 'investigating') return <Badge className="bg-blue-500/15 text-blue-700 border-blue-500/30">Investigating</Badge>;
  if (status === 'resolved') return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30">Resolved</Badge>;
  if (status === 'dismissed') return <Badge variant="inactive">Dismissed</Badge>;
  if (status === 'false_positive') return <Badge variant="inactive">False positive</Badge>;
  return <Badge>{status}</Badge>;
}

function Th({ children, className }: { children: any; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: any; className?: string }) {
  return <td className={`px-3 py-2 ${className ?? ''}`}>{children}</td>;
}

// ─────────────────────────────────────────────────────────────────
// Detail sheet
// ─────────────────────────────────────────────────────────────────
function AnomalyDetailSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useAnomaly(id);
  const update = useUpdateAnomaly();
  const [notes, setNotes] = useState('');

  async function setStatus(status: string) {
    if (!id) return;
    try {
      await update.mutateAsync({ id, status, resolution_notes: notes || undefined });
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? err?.message ?? 'Update failed');
    }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('anomalies.detail.title')}</SheetTitle>
        </SheetHeader>

        {isLoading || !data ? (
          <div className="py-12 text-center text-muted-foreground">{t('common.loading')}</div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Severity strip */}
            <div className="flex items-center gap-2">
              <SeverityBadge severity={data.severity} />
              <StatusBadge status={data.status} />
              <span className="text-xs text-muted-foreground ml-auto font-mono">
                {ruleCodeFromType(data.anomaly_type)}
              </span>
            </div>

            <div>
              <h3 className="text-lg font-semibold">{data.title}</h3>
              {data.description ? <p className="text-sm text-muted-foreground mt-1">{data.description}</p> : null}
            </div>

            {/* What was expected vs actual */}
            {(data.expected_value || data.actual_value) ? (
              <div className="rounded-md border border-border bg-muted/30 p-3 grid grid-cols-2 gap-3 text-sm">
                {data.expected_value ? (
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">{t('anomalies.detail.expected')}</div>
                    <div className="mt-0.5 text-emerald-700 dark:text-emerald-300">{data.expected_value}</div>
                  </div>
                ) : null}
                {data.actual_value ? (
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">{t('anomalies.detail.actual')}</div>
                    <div className="mt-0.5 text-red-700 dark:text-red-300">{data.actual_value}</div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Linked transaction */}
            {data.fuel_at ? (
              <div className="rounded-md border border-border p-3 text-sm space-y-1">
                <div className="text-xs uppercase text-muted-foreground">{t('anomalies.detail.linkedTxn')}</div>
                <div><strong>{t('anomalies.detail.date')}:</strong> {new Date(data.fuel_at).toLocaleString()}</div>
                <div><strong>{t('anomalies.detail.station')}:</strong> {data.fuel_station ?? '—'}</div>
                <div><strong>{t('anomalies.detail.amount')}:</strong> {Number(data.fuel_total_sar ?? 0).toLocaleString()} SAR · {Number(data.fuel_liters ?? 0).toFixed(1)} L</div>
                <div><strong>{t('anomalies.detail.vehicle')}:</strong> {data.vehicle_plate ?? data.vehicle_code ?? '—'}</div>
                <div><strong>{t('anomalies.detail.driver')}:</strong> {data.driver_name ?? data.fuel_provider_driver ?? '—'}</div>
                {data.impact_sar ? <div><strong>{t('anomalies.detail.estLoss')}:</strong> <span className="text-red-700 dark:text-red-300">{Number(data.impact_sar).toLocaleString()} SAR</span></div> : null}
              </div>
            ) : null}

            {/* Recipients */}
            {data.recipients?.length ? (
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">{t('anomalies.detail.recipients')}</div>
                <div className="flex flex-wrap gap-2">
                  {data.recipients.map((r: any) => (
                    <Badge key={r.id} variant="inactive" className="text-xs">
                      {r.user_name} <span className="opacity-60">· {r.role}</span>
                    </Badge>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 italic">
                  {t('anomalies.detail.recipientsNote')}
                </div>
              </div>
            ) : null}

            {/* Resolution form */}
            {data.status === 'open' || data.status === 'investigating' ? (
              <div className="space-y-3 border-t border-border pt-4">
                <div>
                  <label className="text-sm font-medium">{t('anomalies.detail.notes')}</label>
                  <textarea
                    className="mt-1 w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('anomalies.detail.notesPlaceholder') as string}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setStatus('resolved')} disabled={update.isPending}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('anomalies.detail.resolve')}
                  </Button>
                  <Button variant="outline" onClick={() => setStatus('false_positive')} disabled={update.isPending}>
                    {t('anomalies.detail.falsePositive')}
                  </Button>
                  <Button variant="outline" onClick={() => setStatus('investigating')} disabled={update.isPending}>
                    {t('anomalies.detail.investigate')}
                  </Button>
                  <Button variant="outline" onClick={() => setStatus('dismissed')} disabled={update.isPending}>
                    {t('anomalies.detail.dismiss')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm border-t border-border pt-4 space-y-1">
                <div><strong>{t('anomalies.detail.resolvedAt')}:</strong> {data.resolved_at ? new Date(data.resolved_at).toLocaleString() : '—'}</div>
                {data.resolution_notes ? <div className="italic text-muted-foreground">"{data.resolution_notes}"</div> : null}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
